import React from 'react'
import * as state from '../../states'

import {
	IAffiliate,
	ERoleAccess,
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconEdit,
	UiMoreTextInfo,
	headCellFormatterSortable,
	ESortOrder,
	NotesIconBtn
} from '../ui'
import { useAtomValue } from 'jotai'
import { AFFILIATE_PAGE_SORT_FIELDS, IAffiliateSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import dt from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'
import { AgencyPercentBox, AgencyPercentValue, AgencyPercentInfo } from '../revenueAgency'


type IAffiliateListTableProps = {
	items: IAffiliate[];
	skip: number;
	sortBy: IAffiliateSortField;
	setSortBy: (sortBy: IAffiliateSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IAffiliate, e: React.MouseEvent) => void;
	onEditNotes?: (item: IAffiliate, e: React.MouseEvent) => void;
}

export const AffiliateListTable = (props: IAffiliateListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onEditNotes
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const revenueAgencyListTableConfig: IUiTableConfig<IAffiliate>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.affiliate_list_table.column_id)
		},
		{
			key: 'name',
			title: t(ALL_LANG_KEY.affiliate_list_table.column_name)
		},
		{
			key: 'percent',
			title: t(ALL_LANG_KEY.affiliate_list_table.column_percent),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const valueFormat: Array<React.ReactNode> = rowData.percents?.map((item, index, arr) => {
					const at = item.at ?? null
					const nextAt = item.nextAt ?? null
					const percent = helpers.formatAmount(item.value)
					const atFormat = dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'
					// subtract 1 day from nextAt date for regular CRM users (not a mathematicians)
					const nextAtFormat = dt.utc(nextAt).isValid() ? dt.utc(nextAt).subtract(1, 'day').format('L') : '∞'

					return <AgencyPercentBox>
						<AgencyPercentValue>{percent}%</AgencyPercentValue>
						<AgencyPercentInfo>
							({atFormat} - {nextAtFormat}){arr.length - 1 !== index ? ',' : null}
						</AgencyPercentInfo>
					</AgencyPercentBox>
				}) ?? []
				// show only 1 default percent in collapsed state
				return <UiMoreTextInfo value={valueFormat}
				                       withSeparator={false}
				                       limitCollapsed={1}
				/>
			}
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.affiliate_list_table.column_notes),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData }) => {
				const val = rowData?.notes

				return <>
					{
						onEditNotes && !!$access.$(ERoleAccess.AffiliateList)
							? (
								<NotesIconBtn value={val}
								              onClick={(e) => onEditNotes && onEditNotes(rowData, e)}
								/>
							)
							: null
					}
				</>
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>
						{
							!!onEdit && !!$access.$(ERoleAccess.AffiliateModify)
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const revenueAgencyListTableConfigSortable: IUiTableConfig<IAffiliate>[] = revenueAgencyListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = AFFILIATE_PAGE_SORT_FIELDS.includes(configItem.key as IAffiliateSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('AffiliateListTable:: ', { revenueAgencyListTableConfig, revenueAgencyListTableConfigSortable, items })
	const tableConfig = revenueAgencyListTableConfigSortable

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}